import { LanguageProvider, useTranslate } from '@c3po/i18n'
import Loader from '@kawaui/loader'
import { LocaleProvider } from '@kawaui/locale'
import { useAuthWithError } from '@narf/auth'
import ExpiredSessionError from '@narf/error'
import { useUnauth } from '@narf/unauth'
import * as Sentry from '@sentry/nextjs'

import HeadBase from 'next/head'
import React from 'react'

import { UNAUTH_ERROR } from 'constants/index'
import { LoaderWrapper } from 'styles/pages/_app'

type Brand = 'Privalia' | 'Veepee'

type DefaultLanguageProps = {
  children: React.ReactNode
}

const DefaultLangauge = ({ children }: DefaultLanguageProps) => {
  const { locale } = useUnauth()
  // @ts-ignore
  return <LanguageProvider locale={locale}>{children}</LanguageProvider>
}

const url = (site: string) => `https://static.vente-privee.com/vp5/_web/site/vp/_common/favico/${site}/favicon.ico`
const PRIVALIA_FAVICON = url('privalia')
const VEEPEE_FAVICON = url('veepee')
const PURPLE_FAVICON = url('purple')

const getTitle = (isPrivalia: boolean): Brand => (isPrivalia ? 'Privalia' : 'Veepee')
const getFavicon = (siteId: number): string => {
  switch (siteId) {
    case 2:
    case 7:
      return PURPLE_FAVICON
    case 66:
    case 69:
      return PRIVALIA_FAVICON
    default:
      return VEEPEE_FAVICON
  }
}

const getIsPrivalia = (siteId: number) => [66, 69].includes(siteId)
// note(fernando.cervera): this check should be either be on the c3po library or the service itself
const getIsPrivaliaLocale = (siteId, locale) => {
  const isPrivalia = getIsPrivalia(siteId)
  if (isPrivalia && !['es-ES', 'it-IT'].includes(locale)) return false
  return isPrivalia
}

const useHead = ({ pageNameKey, siteId }: { pageNameKey?: string; siteId: number }) => {
  const isPrivalia = getIsPrivalia(siteId)

  const [title, setTitle] = React.useState<string>()
  const [favicon, setFavicon] = React.useState<string>()

  const { i18n } = useTranslate()

  React.useEffect(() => {
    if (siteId) {
      const siteName = getTitle(isPrivalia)

      setTitle(pageNameKey ? `${siteName} - ${i18n(pageNameKey)}` : siteName)
      setFavicon(getFavicon(siteId))
    }
  }, [isPrivalia, siteId, i18n])

  return [title, favicon]
}

type HeadProps = {
  pageNameKey?: string
  siteId: number
}

const Head = ({ pageNameKey, siteId = undefined }: HeadProps) => {
  const [title, favicon] = useHead({ pageNameKey, siteId })
  return (
    <HeadBase>
      <link rel="icon" type="image/x-icon" href={favicon} />
      {title && <title>{title}</title>}
    </HeadBase>
  )
}

type HandleAuthProps = {
  children: React.ReactNode
  pageNameKey?: string
}

export const HandleAuth = ({ children, pageNameKey }: HandleAuthProps) => {
  const [openExpSession, setOpenExpSession] = React.useState(true)
  const { member, error } = useAuthWithError()
  const unauth = useUnauth()
  const siteId = member?.siteId || unauth?.siteId

  const onCloseExpSession = () => setOpenExpSession(false)
  if (!unauth) return null
  if (!member && !error)
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    )
  if (error || !member) {
    return (
      <DefaultLangauge>
        <Head siteId={siteId} />
        <ExpiredSessionError
          {...{
            open: openExpSession,
            onReAuth: onCloseExpSession,
          }}
          {...UNAUTH_ERROR}
        />
      </DefaultLangauge>
    )
  }

  const isPrivaliaLocale = getIsPrivaliaLocale(siteId, member.locale)

  Sentry.configureScope((scope) => {
    scope.setUser({
      id: member.memberId,
    })
    scope.setTag('siteIdFromMember', member.siteId)
    scope.setTag('localeFromMember', member.locale)
  })

  return (
    // @ts-ignore
    <LanguageProvider locale={member.locale} isPrivalia={isPrivaliaLocale}>
      <Head pageNameKey={pageNameKey} siteId={siteId} />
      {/* @ts-ignore */}
      <LocaleProvider currency="EUR" locale={member.locale}>
        {children}
      </LocaleProvider>
    </LanguageProvider>
  )
}
